import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Home, 
  Building2, 
  Settings, 
  Smartphone, 
  Server, 
  Cpu,
  Cloud,
  Shield,
  Users,
  Phone,
  Mail,
  Clock,
  ChevronRight,
  Menu,
  X,
  Check,
  ArrowRight,
  Wifi,
  MonitorSmartphone,
  Zap,
  Lock,
  Save,
  TestTube,
  LogOut,
  Inbox,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ==================== MAIN WEBSITE COMPONENTS ====================

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#chi-siamo", label: "Chi Siamo" },
    { href: "#servizi", label: "Servizi" },
    { href: "#obc", label: "OBC" },
    { href: "#obc-cloud", label: "OBC Cloud" },
    { href: "#contatti", label: "Contatti" },
  ];

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3" data-testid="logo">
            <div className="h-12 w-12 rounded-lg overflow-hidden bg-black flex items-center justify-center">
              <img 
                src="/logo.png" 
                alt="INTELLIS Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">
              INTELLIS Srl
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-600 hover:text-brand-orange transition-colors duration-200 font-medium text-sm"
                data-testid={`nav-link-${link.href.replace('#', '')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button 
              className="bg-brand-orange hover:bg-brand-orange-dark text-white rounded-lg px-6"
              data-testid="cta-button"
              onClick={() => document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Contattaci
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 px-4 text-slate-700 hover:text-brand-orange hover:bg-slate-50 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-blue-50"></div>
      <div className="absolute inset-0 section-pattern opacity-50"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="animate-slideInLeft">
            <div className="inline-flex items-center gap-2 bg-brand-orange/10 text-brand-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Dal 1996 leader nella Building Automation
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Soluzioni <span className="text-brand-orange">Domotiche</span> e Building Automation
            </h1>
            
            <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
              INTELLIS srl offre soluzioni all'avanguardia per la Home & Building Automation 
              utilizzando il sistema KNX come bus di riferimento, integrando sistemi di sicurezza, 
              HVAC, multimediali e video.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                className="bg-brand-orange hover:bg-brand-orange-dark text-white rounded-lg px-8 py-6 text-base shadow-lg hover:shadow-xl transition-all"
                onClick={() => document.getElementById('servizi')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-primary"
              >
                Scopri i Servizi
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="border-slate-200 hover:border-brand-blue hover:bg-brand-blue/5 text-slate-700 rounded-lg px-8 py-6 text-base"
                onClick={() => document.getElementById('obc')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-secondary"
              >
                Il Sistema OBC
              </Button>
            </div>
            
            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-slate-200">
              <div>
                <div className="text-3xl font-heading font-bold text-slate-900">30+</div>
                <div className="text-sm text-slate-500">Anni di Esperienza</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-slate-900">500+</div>
                <div className="text-sm text-slate-500">Progetti Realizzati</div>
              </div>
              <div>
                <div className="text-3xl font-heading font-bold text-slate-900">KNX</div>
                <div className="text-sm text-slate-500">Certificazione</div>
              </div>
            </div>
          </div>
          
          {/* Image/Visual */}
          <div className="relative animate-slideInRight animation-delay-200">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop" 
                alt="Smart Home Automation"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Sistema OBC</div>
                    <div className="text-sm text-slate-500">Controllo Completo</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Chi Siamo Section
const ChiSiamoSection = () => {
  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Esperto",
      description: "Professionisti con decenni di esperienza nel settore"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Affidabilità",
      description: "Oltre 30 anni di attività e centinaia di clienti soddisfatti"
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "Innovazione",
      description: "Sempre aggiornati sulle ultime tecnologie del settore"
    }
  ];

  return (
    <section id="chi-siamo" className="py-24 lg:py-32 bg-slate-50" data-testid="chi-siamo-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop" 
              alt="Team INTELLIS"
              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
            />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-brand-orange rounded-2xl -z-10"></div>
          </div>
          
          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-brand-orange/10 text-brand-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
              Chi Siamo
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Leader nella Building Automation dal 1996
            </h2>
            
            <p className="text-slate-600 mb-6 leading-relaxed">
              <strong>INTELLIS srl</strong> opera nel settore della Home & Building Automation sin dal 1996 
              mettendo a disposizione della propria clientela l'esperienza maturata negli anni sulla 
              progettazione e realizzazione di sistemi ad alto livello di integrazione tecnologica.
            </p>
            
            <p className="text-slate-600 mb-8 leading-relaxed">
              L'esperienza maturata negli anni e la conoscenza delle diverse tecnologie consente ad 
              INTELLIS di seguire il cliente nella ricerca e valutazione delle soluzioni ad alto 
              contenuto tecnologico più appropriate allo scopo prefissato, favorendo una visione 
              dei singoli sotto-sistemi come un unico sistema più grande e complesso: 
              <strong> l'Edificio Intelligente</strong>.
            </p>
            
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-orange/10 rounded-lg flex items-center justify-center text-brand-orange flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{feature.title}</h3>
                    <p className="text-sm text-slate-500">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Servizi Section
const ServiziSection = () => {
  const services = [
    {
      icon: <Home className="w-8 h-8" />,
      title: "Domotica Residenziale",
      description: "Soluzioni complete per la casa intelligente: controllo luci, tapparelle, clima, sicurezza e multimediale.",
      features: ["Controllo Luci", "Automazione Tapparelle", "Gestione Clima"]
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Building Automation",
      description: "Sistemi avanzati per edifici commerciali e industriali con integrazione completa di tutti i sottosistemi.",
      features: ["Gestione Energetica", "Controllo Accessi", "Monitoraggio"]
    },
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "Sistema KNX",
      description: "Progettazione e installazione di impianti basati sullo standard internazionale KNX per building automation.",
      features: ["Standard Mondiale", "Interoperabilità", "Scalabilità"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Sicurezza Integrata",
      description: "Integrazione di sistemi antintrusione, videosorveglianza e controllo accessi nel sistema domotico.",
      features: ["Antintrusione", "TVCC", "Controllo Accessi"]
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "HVAC Control",
      description: "Gestione intelligente degli impianti di climatizzazione per comfort ottimale e risparmio energetico.",
      features: ["Comfort Ottimale", "Risparmio Energetico", "Zone Control"]
    },
    {
      icon: <MonitorSmartphone className="w-8 h-8" />,
      title: "Sistemi Multimediali",
      description: "Integrazione di sistemi audio/video multiroom e controllo di home theater e sale conferenze.",
      features: ["Audio Multiroom", "Home Theater", "Sale Conferenze"]
    }
  ];

  return (
    <section id="servizi" className="py-24 lg:py-32 bg-white" data-testid="servizi-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-brand-orange/10 text-brand-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
            I Nostri Servizi
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Soluzioni Complete per ogni Esigenza
          </h2>
          <p className="text-slate-600">
            Dalla progettazione alla realizzazione, offriamo servizi completi per la 
            home e building automation con le tecnologie più avanzate.
          </p>
        </div>
        
        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="group border-slate-100 hover:border-brand-orange/30 hover:shadow-lg transition-all duration-300"
              data-testid={`service-card-${index}`}
            >
              <CardHeader>
                <div className="w-16 h-16 bg-slate-50 group-hover:bg-brand-orange/10 rounded-xl flex items-center justify-center text-slate-700 group-hover:text-brand-orange transition-colors mb-4">
                  {service.icon}
                </div>
                <CardTitle className="text-xl font-heading text-slate-900">
                  {service.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, idx) => (
                    <span 
                      key={idx}
                      className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// OBC Section
const OBCSection = () => {
  const features = [
    {
      icon: <Server className="w-6 h-6" />,
      title: "Sistema Linux-Based",
      description: "Affidabilità e stabilità di un sistema operativo enterprise per un funzionamento 24/7"
    },
    {
      icon: <Wifi className="w-6 h-6" />,
      title: "Accesso Web-Based",
      description: "Interfaccia intuitiva accessibile via browser da PC, Tablet e Smartphone ovunque ti trovi"
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "Integrazione KNX",
      description: "Piena compatibilità con lo standard mondiale KNX per building automation"
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Multi-Sistema",
      description: "Integrazione completa di HVAC, sicurezza, illuminazione, multimediale e video"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Sicurezza Integrata",
      description: "Gestione centralizzata di antintrusione, videosorveglianza e controllo accessi"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Risparmio Energetico",
      description: "Monitoraggio e ottimizzazione dei consumi per una gestione efficiente dell'energia"
    }
  ];

  return (
    <section id="obc" className="py-24 lg:py-32 bg-brand-blue text-white" data-testid="obc-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-brand-orange/20 text-brand-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
              Il Nostro Prodotto di Punta
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
              OBC – Open Building Control
            </h2>
            
            <p className="text-blue-100 mb-4 leading-relaxed text-lg">
              <strong className="text-white">OBC (Open Building Control)</strong> è il cuore tecnologico delle nostre soluzioni di building automation: 
              un sistema di supervisione e controllo <strong className="text-white">Web-Based</strong> basato su sistema operativo Linux, 
              progettato per fungere da unità centrale di gestione dell'intero edificio.
            </p>
            
            <p className="text-blue-200 mb-4 leading-relaxed">
              Nato da oltre <strong className="text-white">10 anni di ricerca, sviluppo e installazioni sul campo</strong>, 
              OBC rappresenta la sintesi perfetta tra affidabilità industriale e semplicità d'uso. 
              Il sistema è accessibile via web mediante qualsiasi browser moderno, permettendo il controllo 
              completo dell'edificio da PC, Tablet e Smartphone, sia in locale che da remoto.
            </p>
            
            <p className="text-blue-200 mb-4 leading-relaxed">
              OBC utilizza il <strong className="text-white">bus KNX</strong> come protocollo di riferimento per la comunicazione 
              con i dispositivi di campo, garantendo interoperabilità con migliaia di prodotti certificati 
              di oltre 500 produttori mondiali. Questa scelta tecnologica assicura flessibilità, 
              espandibilità e protezione dell'investimento nel tempo.
            </p>
            
            <p className="text-blue-200 mb-6 leading-relaxed">
              Il sistema integra in un'unica piattaforma la gestione di <strong className="text-white">illuminazione, 
              climatizzazione (HVAC), tapparelle e tende, sistemi di sicurezza, antintrusione, 
              videosorveglianza, controllo accessi, diffusione sonora e sistemi multimediali</strong>. 
              Grazie a scenari programmabili, schedulazioni temporali e logiche automatiche, 
              OBC trasforma l'edificio in un ambiente intelligente, confortevole e a basso consumo energetico.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-brand-orange/20 rounded-lg flex items-center justify-center text-brand-orange flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm mb-1">{feature.title}</h3>
                    <p className="text-xs text-blue-200">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              className="bg-brand-orange hover:bg-brand-orange-dark text-white rounded-lg px-8 py-6"
              onClick={() => document.getElementById('contatti')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="obc-cta"
            >
              Richiedi una Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          {/* Visual */}
          <div className="relative">
            <div className="bg-brand-blue-dark rounded-2xl p-8 border border-blue-700">
              <img 
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=500&fit=crop" 
                alt="OBC Dashboard Interface"
                className="rounded-xl shadow-2xl w-full object-cover"
              />
              {/* Interface mockup overlay */}
              <div className="absolute top-4 right-4 bg-green-500/20 border border-green-500/50 rounded-full px-3 py-1 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-400">Sistema Attivo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// OBC Cloud Section
const OBCCloudSection = () => {
  return (
    <section id="obc-cloud" className="py-24 lg:py-32 bg-gradient-to-br from-blue-50 to-white" data-testid="obc-cloud-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="flex justify-center lg:justify-start order-2 lg:order-1">
            <div className="relative">
              <img 
                src="/obc-cloud.png" 
                alt="OBC Cloud" 
                className="w-80 h-80 object-contain drop-shadow-2xl"
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-brand-orange/10 text-brand-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Cloud className="w-4 h-4" />
              Disponibile Ora
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              OBC Cloud
            </h2>
            
            <p className="text-slate-600 text-lg mb-4 leading-relaxed">
              <strong>OBC Cloud</strong> rappresenta l'evoluzione naturale del nostro sistema OBC, 
              portando tutte le funzionalità di controllo e supervisione degli edifici direttamente nel cloud.
            </p>
            
            <p className="text-slate-600 mb-4 leading-relaxed">
              Grazie a OBC Cloud, potrai accedere al tuo sistema di building automation da qualsiasi 
              luogo e in qualsiasi momento, utilizzando il tuo smartphone, tablet o computer. 
              Il monitoraggio in tempo reale ti permette di tenere sotto controllo consumi energetici, 
              stato degli impianti e parametri ambientali con un semplice tocco.
            </p>
            
            <p className="text-slate-600 mb-6 leading-relaxed">
              La piattaforma supporta la gestione multi-sito, ideale per chi deve supervisionare 
              più edifici contemporaneamente, offrendo dashboard personalizzabili, notifiche push 
              e report dettagliati per un controllo completo e centralizzato.
            </p>
            
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
                <Cloud className="w-5 h-5 text-brand-blue" />
                <span className="text-sm text-slate-600">Accesso Remoto</span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
                <Shield className="w-5 h-5 text-brand-blue" />
                <span className="text-sm text-slate-600">Sicurezza Avanzata</span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
                <Smartphone className="w-5 h-5 text-brand-blue" />
                <span className="text-sm text-slate-600">App Mobile</span>
              </div>
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
                <Building2 className="w-5 h-5 text-brand-blue" />
                <span className="text-sm text-slate-600">Multi-Sito</span>
              </div>
            </div>
            
            {/* App Links */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm mb-6">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                Scarica l'App OBC Cloud
              </h3>
              <div className="flex flex-wrap gap-4">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.obccloud.mobile" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 rounded-xl transition-colors"
                  data-testid="play-store-link"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div>
                    <div className="text-xs opacity-75">Disponibile su</div>
                    <div className="font-semibold">Google Play</div>
                  </div>
                </a>
                <a 
                  href="https://apps.apple.com/app/obc-cloud/id6760346325" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 rounded-xl transition-colors"
                  data-testid="app-store-link"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                  </svg>
                  <div>
                    <div className="text-xs opacity-75">Scarica su</div>
                    <div className="font-semibold">App Store</div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Website CTA */}
            <Button 
              className="bg-brand-orange hover:bg-brand-orange-dark text-white rounded-lg px-8 py-6 text-base"
              onClick={() => window.open('https://www.cloud.obcevo.com', '_blank')}
              data-testid="obc-cloud-website"
            >
              Vai al Sito OBC Cloud
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContattiSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    oggetto: '',
    messaggio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ nome: '', email: '', oggetto: '', messaggio: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-5 h-5" />,
      title: "Telefono",
      value: "+39 055 8367477",
      href: "tel:+390558367477"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Email",
      value: "info@intellis.it",
      href: "mailto:info@intellis.it"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Assistenza Tecnica",
      value: "tecnico@intellis.it",
      href: "mailto:tecnico@intellis.it"
    },
    {
      icon: <Mail className="w-5 h-5" />,
      title: "Amministrazione",
      value: "segreteria@intellis.it",
      href: "mailto:segreteria@intellis.it"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Orari Assistenza",
      value: "Lun-Ven: 8:30 - 17:00"
    }
  ];

  return (
    <section id="contatti" className="py-24 lg:py-32 bg-white" data-testid="contatti-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <div className="inline-flex items-center gap-2 bg-brand-orange/10 text-brand-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
              Contatti
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Parliamo del Tuo Progetto
            </h2>
            
            <p className="text-slate-600 mb-8 leading-relaxed">
              Contattaci per una consulenza gratuita. Il nostro team di esperti è a disposizione 
              per valutare le soluzioni più adatte alle tue esigenze.
            </p>
            
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-brand-orange flex-shrink-0">
                    {info.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{info.title}</h3>
                    {info.href ? (
                      <a 
                        href={info.href} 
                        className="text-slate-600 hover:text-brand-orange transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-slate-600">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Hours Card */}
            <Card className="mt-8 border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Orari di Servizio</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Assistenza Tecnica</span>
                    <span className="text-slate-900 font-medium">Lun-Ven: 8:30 - 17:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Amministrazione</span>
                    <span className="text-slate-900 font-medium">Lun-Ven: 9:30 - 12:30</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Contact Form */}
          <div>
            <Card className="border-slate-100 shadow-lg">
              <CardContent className="p-8">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">
                  Invia un Messaggio
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Nome *
                      </label>
                      <Input 
                        type="text"
                        required
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        placeholder="Il tuo nome"
                        className="border-slate-200 focus:border-brand-blue"
                        data-testid="input-nome"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <Input 
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="La tua email"
                        className="border-slate-200 focus:border-brand-blue"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Oggetto *
                    </label>
                    <Input 
                      type="text"
                      required
                      value={formData.oggetto}
                      onChange={(e) => setFormData({...formData, oggetto: e.target.value})}
                      placeholder="Oggetto del messaggio"
                      className="border-slate-200 focus:border-brand-blue"
                      data-testid="input-oggetto"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Messaggio *
                    </label>
                    <Textarea 
                      required
                      rows={5}
                      value={formData.messaggio}
                      onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
                      placeholder="Descrivi il tuo progetto o la tua richiesta..."
                      className="border-slate-200 focus:border-brand-blue resize-none"
                      data-testid="input-messaggio"
                    />
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2" data-testid="form-success">
                      <Check className="w-5 h-5" />
                      Messaggio inviato con successo!
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="form-error">
                      Si è verificato un errore. Riprova più tardi.
                    </div>
                  )}
                  
                  <Button 
                    type="submit"
                    className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white rounded-lg py-6"
                    disabled={isSubmitting}
                    data-testid="submit-button"
                  >
                    {isSubmitting ? 'Invio in corso...' : 'Invia Messaggio'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-brand-blue text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg overflow-hidden bg-black flex items-center justify-center">
                <img 
                  src="/logo.png" 
                  alt="INTELLIS Logo" 
                  className="h-8 w-auto object-contain"
                />
              </div>
              <span className="font-heading font-bold text-xl">INTELLIS srl</span>
            </div>
            <p className="text-blue-100 max-w-md leading-relaxed">
              Soluzioni Domotiche e Building Automation dal 1996. 
              Leader nella progettazione e realizzazione di sistemi ad alto livello di integrazione tecnologica.
            </p>
          </div>
          
          {/* Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Link Rapidi</h4>
            <ul className="space-y-2">
              <li><a href="#chi-siamo" className="text-blue-100 hover:text-brand-orange transition-colors">Chi Siamo</a></li>
              <li><a href="#servizi" className="text-blue-100 hover:text-brand-orange transition-colors">Servizi</a></li>
              <li><a href="#obc" className="text-blue-100 hover:text-brand-orange transition-colors">OBC</a></li>
              <li><a href="#obc-cloud" className="text-blue-100 hover:text-brand-orange transition-colors">OBC Cloud</a></li>
              <li><a href="#contatti" className="text-blue-100 hover:text-brand-orange transition-colors">Contatti</a></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold mb-4">Contatti</h4>
            <ul className="space-y-2 text-blue-100">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-brand-orange" />
                <a href="tel:+390558367477" className="hover:text-brand-orange transition-colors">+39 055 8367477</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-brand-orange" />
                <a href="mailto:info@intellis.it" className="hover:text-brand-orange transition-colors">info@intellis.it</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-brand-orange" />
                <a href="mailto:tecnico@intellis.it" className="hover:text-brand-orange transition-colors">tecnico@intellis.it</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-brand-orange" />
                <a href="mailto:segreteria@intellis.it" className="hover:text-brand-orange transition-colors">segreteria@intellis.it</a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom */}
        <div className="border-t border-blue-700 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-blue-200 text-sm">
            © {new Date().getFullYear()} INTELLIS srl. Tutti i diritti riservati.
          </p>
          <p className="text-blue-200 text-sm">
            Home & Building Automation dal 1996
          </p>
        </div>
      </div>
    </footer>
  );
};

// ==================== ADMIN PANEL COMPONENTS ====================

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/admin/verify`, {
        auth: { username, password }
      });
      if (response.data.success) {
        onLogin({ username, password });
      }
    } catch (err) {
      setError('Credenziali non valide');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-xl overflow-hidden bg-black flex items-center justify-center">
              <img src="/logo.png" alt="INTELLIS" className="h-12 w-auto object-contain" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl">Pannello Amministrazione</CardTitle>
          <CardDescription>Accedi per gestire le impostazioni del sito</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full bg-brand-slate"
              disabled={isLoading}
              data-testid="admin-login-btn"
            >
              {isLoading ? 'Accesso...' : 'Accedi'}
              <Lock className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminPanel = ({ auth, onLogout }) => {
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'INTELLIS srl',
    smtp_use_tls: true,
    recipient_email: 'info@intellis.it'
  });
  const [messages, setMessages] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newUsername: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Load SMTP config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/smtp`, {
          auth: { username: auth.username, password: auth.password }
        });
        setSmtpConfig(response.data);
      } catch (err) {
        console.error('Error loading SMTP config:', err);
      }
    };
    loadConfig();
  }, [auth]);

  // Load messages
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const response = await axios.get(`${API}/admin/messages`, {
          auth: { username: auth.username, password: auth.password }
        });
        setMessages(response.data.messages || []);
      } catch (err) {
        console.error('Error loading messages:', err);
      }
    };
    loadMessages();
  }, [auth]);

  const handleSaveConfig = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    try {
      await axios.post(`${API}/admin/smtp`, smtpConfig, {
        auth: { username: auth.username, password: auth.password }
      });
      setSaveStatus({ type: 'success', message: 'Configurazione salvata!' });
    } catch (err) {
      setSaveStatus({ type: 'error', message: 'Errore nel salvataggio' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleTestEmail = async () => {
    setIsTesting(true);
    setSaveStatus(null);
    try {
      await axios.post(`${API}/admin/smtp/test`, {}, {
        auth: { username: auth.username, password: auth.password }
      });
      setSaveStatus({ type: 'success', message: 'Email di test inviata con successo!' });
    } catch (err) {
      setSaveStatus({ type: 'error', message: err.response?.data?.detail || 'Errore invio email' });
    } finally {
      setIsTesting(false);
      setTimeout(() => setSaveStatus(null), 5000);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordData.currentPassword) {
      setPasswordStatus({ type: 'error', message: 'Inserisci la password attuale' });
      return;
    }
    if (!passwordData.newPassword) {
      setPasswordStatus({ type: 'error', message: 'Inserisci la nuova password' });
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'Le password non coincidono' });
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordStatus({ type: 'error', message: 'La password deve essere di almeno 6 caratteri' });
      return;
    }

    setIsChangingPassword(true);
    setPasswordStatus(null);

    try {
      await axios.put(`${API}/admin/credentials`, {
        username: passwordData.newUsername || auth.username,
        password: passwordData.newPassword,
        current_password: passwordData.currentPassword
      }, {
        auth: { username: auth.username, password: auth.password }
      });
      
      setPasswordStatus({ type: 'success', message: 'Credenziali aggiornate! Effettua nuovamente il login.' });
      setPasswordData({ currentPassword: '', newUsername: '', newPassword: '', confirmPassword: '' });
      
      // Logout after 2 seconds
      setTimeout(() => {
        onLogout();
      }, 2000);
    } catch (err) {
      setPasswordStatus({ type: 'error', message: err.response?.data?.detail || 'Errore nel cambio password' });
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg overflow-hidden bg-black flex items-center justify-center">
                <img src="/logo.png" alt="INTELLIS" className="h-8 w-auto object-contain" />
              </div>
              <span className="font-heading font-bold text-lg">Admin Panel</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="text-sm text-slate-600 hover:text-brand-orange">
                Torna al sito
              </a>
              <Button variant="outline" size="sm" onClick={onLogout} data-testid="admin-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="smtp" className="space-y-6">
          <TabsList>
            <TabsTrigger value="smtp" data-testid="tab-smtp">
              <Mail className="w-4 h-4 mr-2" />
              Configurazione SMTP
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              <Inbox className="w-4 h-4 mr-2" />
              Messaggi ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Lock className="w-4 h-4 mr-2" />
              Sicurezza
            </TabsTrigger>
          </TabsList>

          {/* SMTP Configuration Tab */}
          <TabsContent value="smtp">
            <Card>
              <CardHeader>
                <CardTitle>Configurazione Server SMTP</CardTitle>
                <CardDescription>
                  Configura il server SMTP per l'invio delle email dal form di contatto
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="smtp_host">Server SMTP *</Label>
                    <Input
                      id="smtp_host"
                      placeholder="smtp.example.com"
                      value={smtpConfig.smtp_host}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_host: e.target.value})}
                      data-testid="smtp-host"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp_port">Porta *</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      placeholder="587"
                      value={smtpConfig.smtp_port}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_port: parseInt(e.target.value) || 587})}
                      data-testid="smtp-port"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp_username">Username *</Label>
                    <Input
                      id="smtp_username"
                      placeholder="user@example.com"
                      value={smtpConfig.smtp_username}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_username: e.target.value})}
                      data-testid="smtp-username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp_password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="smtp_password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={smtpConfig.smtp_password}
                        onChange={(e) => setSmtpConfig({...smtpConfig, smtp_password: e.target.value})}
                        data-testid="smtp-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="smtp_from_email">Email Mittente *</Label>
                    <Input
                      id="smtp_from_email"
                      type="email"
                      placeholder="noreply@intellis.it"
                      value={smtpConfig.smtp_from_email}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_email: e.target.value})}
                      data-testid="smtp-from-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp_from_name">Nome Mittente</Label>
                    <Input
                      id="smtp_from_name"
                      placeholder="INTELLIS srl"
                      value={smtpConfig.smtp_from_name}
                      onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_name: e.target.value})}
                      data-testid="smtp-from-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="recipient_email">Email Destinatario *</Label>
                    <Input
                      id="recipient_email"
                      type="email"
                      placeholder="info@intellis.it"
                      value={smtpConfig.recipient_email}
                      onChange={(e) => setSmtpConfig({...smtpConfig, recipient_email: e.target.value})}
                      data-testid="smtp-recipient"
                    />
                    <p className="text-xs text-slate-500 mt-1">Email dove ricevere i messaggi dal form di contatto</p>
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <Switch
                      id="smtp_use_tls"
                      checked={smtpConfig.smtp_use_tls}
                      onCheckedChange={(checked) => setSmtpConfig({...smtpConfig, smtp_use_tls: checked})}
                      data-testid="smtp-tls"
                    />
                    <Label htmlFor="smtp_use_tls">Usa TLS/STARTTLS</Label>
                  </div>
                </div>

                {saveStatus && (
                  <div className={`p-4 rounded-lg ${saveStatus.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {saveStatus.message}
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <Button onClick={handleSaveConfig} disabled={isSaving} data-testid="save-smtp">
                    <Save className="w-4 h-4 mr-2" />
                    {isSaving ? 'Salvataggio...' : 'Salva Configurazione'}
                  </Button>
                  <Button variant="outline" onClick={handleTestEmail} disabled={isTesting} data-testid="test-smtp">
                    <TestTube className="w-4 h-4 mr-2" />
                    {isTesting ? 'Invio...' : 'Invia Email di Test'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle>Messaggi Ricevuti</CardTitle>
                <CardDescription>
                  Ultimi messaggi ricevuti dal form di contatto
                </CardDescription>
              </CardHeader>
              <CardContent>
                {messages.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun messaggio ricevuto</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <div key={msg.id || index} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold text-slate-900">{msg.nome}</h4>
                            <p className="text-sm text-slate-500">{msg.email}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded-full ${msg.email_sent ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {msg.email_sent ? 'Inviato' : 'In attesa'}
                            </span>
                            <p className="text-xs text-slate-400 mt-1">
                              {new Date(msg.sent_at).toLocaleString('it-IT')}
                            </p>
                          </div>
                        </div>
                        <p className="font-medium text-slate-700 mb-2">{msg.oggetto}</p>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{msg.messaggio}</p>
                        {msg.error_message && (
                          <p className="text-xs text-red-600 mt-2">Errore: {msg.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Cambio Credenziali</CardTitle>
                <CardDescription>
                  Modifica username e password per l'accesso al pannello di amministrazione
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 max-w-md">
                  <div>
                    <Label htmlFor="current_password">Password Attuale *</Label>
                    <div className="relative">
                      <Input
                        id="current_password"
                        type={showCurrentPassword ? "text" : "password"}
                        placeholder="Inserisci la password attuale"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                        data-testid="current-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <Label htmlFor="new_username">Nuovo Username (opzionale)</Label>
                    <Input
                      id="new_username"
                      type="text"
                      placeholder={auth.username}
                      value={passwordData.newUsername}
                      onChange={(e) => setPasswordData({...passwordData, newUsername: e.target.value})}
                      data-testid="new-username"
                    />
                    <p className="text-xs text-slate-500 mt-1">Lascia vuoto per mantenere l'username attuale</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="new_password">Nuova Password *</Label>
                    <div className="relative">
                      <Input
                        id="new_password"
                        type={showNewPassword ? "text" : "password"}
                        placeholder="Inserisci la nuova password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        data-testid="new-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="confirm_password">Conferma Nuova Password *</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      placeholder="Ripeti la nuova password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      data-testid="confirm-password"
                    />
                  </div>
                </div>

                {passwordStatus && (
                  <div className={`p-4 rounded-lg max-w-md ${passwordStatus.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {passwordStatus.message}
                  </div>
                )}

                <Button 
                  onClick={handleChangePassword} 
                  disabled={isChangingPassword}
                  className="bg-brand-orange hover:bg-brand-orange-dark"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {isChangingPassword ? 'Aggiornamento...' : 'Aggiorna Credenziali'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [auth, setAuth] = useState(null);

  const handleLogout = () => {
    setAuth(null);
  };

  if (!auth) {
    return <AdminLogin onLogin={setAuth} />;
  }

  return <AdminPanel auth={auth} onLogout={handleLogout} />;
};

// ==================== MAIN WEBSITE PAGE ====================

const MainWebsite = () => {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <ChiSiamoSection />
        <ServiziSection />
        <OBCSection />
        <OBCCloudSection />
        <ContattiSection />
      </main>
      <Footer />
    </>
  );
};

// ==================== APP ROUTER ====================

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainWebsite />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
